import type { VariantProps } from "@-/com.npmjs.cva";
import type { Role } from "@-/com.npmjs.react-native";
import { GlassContentContext, getGlassInkColor } from "#glass-content-context";
import { TextClassContext } from "#text-class-context";
import { textVariants } from "#text-variants";
import { cn } from "@-/com.npmjs.cn";
import { Slot } from "@rn-primitives/slot";
import { type ComponentProps, type RefAttributes, use } from "react";
import { Platform } from "@-/com.npmjs.react-native";
import { Text as RNText } from "@-/com.npmjs.react-native/text";

type TextVariantProps = VariantProps<typeof textVariants>;

type TextVariant = NonNullable<TextVariantProps["variant"]>;

const ROLE: Partial<Record<TextVariant, Role | "code" | "blockquote">> = {
  blockquote: Platform.select({ web: "blockquote" }),
  code: Platform.select({ web: "code" }),
  h1: "heading",
  h2: "heading",
  h3: "heading",
  h4: "heading",
  link: Platform.select({ web: "link" }),
};

const ARIA_LEVEL: Partial<Record<TextVariant, string>> = {
  h1: "1",
  h2: "2",
  h3: "3",
  h4: "4",
};

export function Text({
  asChild = false,
  className,
  colorVariant = "default",
  style,
  variant = "default",
  ...props
}: ComponentProps<typeof RNText> &
  TextVariantProps &
  RefAttributes<RNText> & {
    asChild?: boolean;
  }) {
  const textClass = use(TextClassContext);
  const Component = asChild ? Slot : RNText;
  // Arbitrary `text-[…]` overrides drop `text-base`'s bundled line height via tailwind-merge,
  // so default to `leading-normal` to keep Latin descenders from clipping. Placed before
  // `className` so an explicit caller `leading-*` still wins via tailwind-merge.
  // Skip for `glyph` (CJK metrics), `small` (`leading-none`), and `p` (`leading-[1.75]`).
  const descenderSafeLineHeight =
    variant === "glyph" || variant === "small" || variant === "p" ? undefined : "leading-normal";
  const mergedClassName = cn(
    textVariants({ colorVariant, variant }),
    textClass,
    descenderSafeLineHeight,
    className,
  );
  // On Liquid Glass a theme ink follows the light/dark appearance iOS gives the glass for its
  // backdrop. A caller's `style` still wins.
  const glassAppearance = use(GlassContentContext);
  const glassInk =
    glassAppearance === null ? undefined : getGlassInkColor(mergedClassName, glassAppearance);
  return (
    <Component
      aria-level={variant ? ARIA_LEVEL[variant] : undefined}
      className={mergedClassName}
      role={variant ? ROLE[variant] : undefined}
      style={glassInk === undefined ? style : [{ color: glassInk }, style]}
      {...(Platform.OS === "android" ? { includeFontPadding: false } : {})}
      {...props}
    />
  );
}
